import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  TablePagination,
  TextField,
  MenuItem,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Visibility,
  Security,
  Block,
  Warning,
  CheckCircle,
  Close,
  AccessTime,
  Memory,
  SmartToy,
  Gavel,
  Shield,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

interface PromptRecord {
  id: number;
  prompt_preview: string;
  response_preview?: string;
  status: 'allowed' | 'blocked' | 'flagged';
  threat_score: number;
  threats_count: number;
  tokens_used?: number;
  model?: string;
  processing_time_ms?: number;
  created_at: string;
  detected_threats?: Array<{
    type: string;
    confidence: number;
    category?: string;
    framework?: string;
  }>;
}

interface PromptDetail {
  id: number;
  prompt_text: string;
  filtered_prompt?: string;
  response_text?: string;
  filtered_response?: string;
  status: string;
  threat_score: number;
  detected_threats: Array<{
    type: string;
    confidence: number;
    category?: string;
  }>;
  tokens_used: number;
  model: string;
  processing_time_ms: number;
  metadata?: any;
  created_at: string;
}

interface HistoryResponse {
  prompts: PromptRecord[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

const PromptHistory: React.FC = () => {
  const [prompts, setPrompts] = useState<PromptRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [frameworkFilter, setFrameworkFilter] = useState<string>('all');
  const [selectedPrompt, setSelectedPrompt] = useState<PromptDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      fetchPromptHistory();
    }
  }, [isAuthenticated, page, rowsPerPage, statusFilter]);

  const fetchPromptHistory = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
      });
      
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }

      const response = await axios.get(`/api/prompt/history?${params}`);
      const data: HistoryResponse = response.data;
      
      setPrompts(data.prompts || []);
      setTotalCount(data.pagination?.total || 0);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching prompt history:', err);
      setError('Failed to load prompt history');
      setPrompts([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchPromptDetail = async (promptId: number) => {
    try {
      setDetailLoading(true);
      setDetailError(null);
      const response = await axios.get(`/api/prompt/${promptId}`);
      setSelectedPrompt(response.data);
    } catch (err: any) {
      console.error('Error fetching prompt detail:', err);
      setDetailError('Failed to load prompt details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleViewDetails = (promptId: number) => {
    fetchPromptDetail(promptId);
  };

  const handleCloseDetail = () => {
    setSelectedPrompt(null);
    setDetailError(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'blocked': return 'error';
      case 'flagged': return 'warning';
      case 'allowed': return 'success';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'blocked': return <Block fontSize="small" />;
      case 'flagged': return <Warning fontSize="small" />;
      case 'allowed': return <CheckCircle fontSize="small" />;
      default: return <Security fontSize="small" />;
    }
  };

  const getThreatColor = (score: number) => {
    if (score >= 0.8) return 'error';
    if (score >= 0.5) return 'warning';
    return 'success';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const truncateText = (text: string, maxLength: number = 100) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const getComplianceFrameworks = (threats: Array<{type: string; category?: string; framework?: string}> = []) => {
    const frameworks = new Set<string>();
    
    threats.forEach(threat => {
      // Check if threat has explicit framework
      if (threat.framework) {
        frameworks.add(threat.framework);
      }
      // Infer framework from threat type
      else if (threat.type) {
        const type = threat.type.toLowerCase();
        if (type.includes('phi') || type.includes('hipaa') || type.includes('patient') || type.includes('medical')) {
          frameworks.add('HIPAA');
        }
        if (type.includes('gdpr') || type.includes('pii') || type.includes('personal') || type.includes('consent')) {
          frameworks.add('GDPR');
        }
        if (type.includes('soc2') || type.includes('confidential') || type.includes('access')) {
          frameworks.add('SOC2');
        }
        if (type.includes('iso') || type.includes('27001')) {
          frameworks.add('ISO27001');
        }
        if (type.includes('nist')) {
          frameworks.add('NIST80053');
        }
      }
    });
    
    return Array.from(frameworks);
  };

  const getFrameworkColor = (framework: string): 'error' | 'warning' | 'info' | 'success' => {
    switch (framework) {
      case 'HIPAA': return 'error';
      case 'GDPR': return 'warning';
      case 'SOC2': return 'info';
      case 'ISO27001': return 'success';
      case 'NIST80053': return 'info';
      default: return 'info';
    }
  };

  const getFrameworkIcon = (framework: string) => {
    switch (framework) {
      case 'HIPAA': return <Shield fontSize="small" />;
      case 'GDPR': return <Gavel fontSize="small" />;
      case 'SOC2': return <Security fontSize="small" />;
      default: return <Security fontSize="small" />;
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading && prompts.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Prompt History
      </Typography>
      
      <Typography variant="body1" color="textSecondary" paragraph>
        View and analyze your prompt processing history, including security analysis results and LLM responses.
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              select
              fullWidth
              label="Status Filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              size="small"
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="allowed">Allowed</MenuItem>
              <MenuItem value="flagged">Flagged</MenuItem>
              <MenuItem value="blocked">Blocked</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              select
              fullWidth
              label="Compliance Framework"
              value={frameworkFilter}
              onChange={(e) => setFrameworkFilter(e.target.value)}
              size="small"
            >
              <MenuItem value="all">All Frameworks</MenuItem>
              <MenuItem value="HIPAA">HIPAA</MenuItem>
              <MenuItem value="GDPR">GDPR</MenuItem>
              <MenuItem value="SOC2">SOC2</MenuItem>
              <MenuItem value="ISO27001">ISO 27001</MenuItem>
              <MenuItem value="NIST80053">NIST 800-53</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Prompt</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Compliance</TableCell>
                <TableCell>Threat Score</TableCell>
                <TableCell>Threats</TableCell>
                <TableCell>Model</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {prompts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Box py={4}>
                      <Security sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Typography color="textSecondary" variant="h6">
                        {loading ? 'Loading...' : 'No prompt history available'}
                      </Typography>
                      <Typography color="textSecondary" variant="body2">
                        Start testing prompts to see your history here
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                prompts
                  .filter(prompt => {
                    // Filter by framework
                    if (frameworkFilter === 'all') return true;
                    const frameworks = getComplianceFrameworks(prompt.detected_threats || []);
                    return frameworks.includes(frameworkFilter);
                  })
                  .map((prompt) => {
                    const frameworks = getComplianceFrameworks(prompt.detected_threats || []);
                    
                    return (
                      <TableRow key={prompt.id} hover>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(prompt.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={prompt.prompt_preview}>
                            <Typography variant="body2">
                              {prompt.prompt_preview}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(prompt.status)}
                            label={prompt.status.toUpperCase()}
                            color={getStatusColor(prompt.status) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={0.5} flexWrap="wrap">
                            {frameworks.length > 0 ? (
                              frameworks.map(framework => (
                                <Chip
                                  key={framework}
                                  icon={getFrameworkIcon(framework)}
                                  label={framework}
                                  color={getFrameworkColor(framework)}
                                  size="small"
                                  variant="outlined"
                                />
                              ))
                            ) : (
                              <Typography variant="body2" color="textSecondary">
                                None
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={`${(prompt.threat_score * 100).toFixed(1)}%`}
                            color={getThreatColor(prompt.threat_score) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {prompt.threats_count > 0 ? (
                            <Chip
                              label={`${prompt.threats_count} threat${prompt.threats_count > 1 ? 's' : ''}`}
                              size="small"
                              variant="outlined"
                              color="error"
                            />
                          ) : (
                            <Typography variant="body2" color="textSecondary">
                              None
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {prompt.model || 'N/A'}
                          </Typography>
                          {prompt.tokens_used && (
                            <Typography variant="caption" color="textSecondary">
                              {prompt.tokens_used} tokens
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View Details">
                            <IconButton 
                              size="small"
                              onClick={() => handleViewDetails(prompt.id)}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {prompts.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Paper>

      {/* Detail Dialog */}
      <Dialog
        open={!!selectedPrompt}
        onClose={handleCloseDetail}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Prompt Details</Typography>
            <IconButton onClick={handleCloseDetail} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {detailLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : detailError ? (
            <Alert severity="error">{detailError}</Alert>
          ) : selectedPrompt ? (
            <Box>
              {/* Status and Metadata */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Status</Typography>
                    <Chip
                      icon={getStatusIcon(selectedPrompt.status)}
                      label={selectedPrompt.status.toUpperCase()}
                      color={getStatusColor(selectedPrompt.status) as any}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Threat Score</Typography>
                    <Chip
                      label={`${(selectedPrompt.threat_score * 100).toFixed(1)}%`}
                      color={getThreatColor(selectedPrompt.threat_score) as any}
                    />
                  </Paper>
                </Grid>
              </Grid>

              {/* Processing Info */}
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>Processing Information</Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><SmartToy /></ListItemIcon>
                    <ListItemText primary="Model" secondary={selectedPrompt.model} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><Memory /></ListItemIcon>
                    <ListItemText primary="Tokens Used" secondary={selectedPrompt.tokens_used} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><AccessTime /></ListItemIcon>
                    <ListItemText 
                      primary="Processing Time" 
                      secondary={`${selectedPrompt.processing_time_ms}ms`} 
                    />
                  </ListItem>
                </List>
              </Paper>

              {/* Detected Threats */}
              {selectedPrompt.detected_threats && selectedPrompt.detected_threats.length > 0 && (
                <Paper sx={{ p: 2, mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>Detected Threats</Typography>
                  <List dense>
                    {selectedPrompt.detected_threats.map((threat, index) => (
                      <ListItem key={index}>
                        <ListItemIcon><Warning color="warning" /></ListItemIcon>
                        <ListItemText
                          primary={threat.type.replace(/_/g, ' ').toUpperCase()}
                          secondary={`Confidence: ${(threat.confidence * 100).toFixed(1)}%${threat.category ? ` | Category: ${threat.category}` : ''}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              )}

              {/* Original Prompt */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Original Prompt</Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    whiteSpace: 'pre-wrap', 
                    backgroundColor: 'grey.50', 
                    p: 2, 
                    borderRadius: 1,
                    maxHeight: 200,
                    overflow: 'auto'
                  }}
                >
                  {selectedPrompt.prompt_text}
                </Typography>
              </Paper>

              {/* Filtered Prompt (if different) */}
              {selectedPrompt.filtered_prompt && selectedPrompt.filtered_prompt !== selectedPrompt.prompt_text && (
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Filtered Prompt</Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      whiteSpace: 'pre-wrap', 
                      backgroundColor: 'warning.light', 
                      p: 2, 
                      borderRadius: 1,
                      maxHeight: 200,
                      overflow: 'auto'
                    }}
                  >
                    {selectedPrompt.filtered_prompt}
                  </Typography>
                </Paper>
              )}

              {/* Response */}
              {selectedPrompt.response_text && (
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>LLM Response</Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      whiteSpace: 'pre-wrap', 
                      backgroundColor: 'grey.50', 
                      p: 2, 
                      borderRadius: 1,
                      maxHeight: 300,
                      overflow: 'auto'
                    }}
                  >
                    {selectedPrompt.response_text}
                  </Typography>
                </Paper>
              )}

              {/* Filtered Response (if different) */}
              {selectedPrompt.filtered_response && selectedPrompt.filtered_response !== selectedPrompt.response_text && (
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Filtered Response</Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      whiteSpace: 'pre-wrap', 
                      backgroundColor: 'success.light', 
                      p: 2, 
                      borderRadius: 1,
                      maxHeight: 300,
                      overflow: 'auto'
                    }}
                  >
                    {selectedPrompt.filtered_response}
                  </Typography>
                </Paper>
              )}

              {/* Timestamp */}
              <Typography variant="caption" color="textSecondary">
                Created: {formatDate(selectedPrompt.created_at)}
              </Typography>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetail}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PromptHistory; 